<template>
  <div class="permissions-table-container">
    <div class="table-header d-flex justify-content-between align-items-center mb-4">
      <h3 class="table-title">Permissions</h3>
      <div class="table-controls">
        <div class="input-group">
          <input 
            type="text" 
            class="form-control" 
            placeholder="Search permissions..." 
            v-model="searchQuery"
            @input="filterPermissions"
          >
          <button class="btn btn-outline-secondary" type="button">
            <i class="fas fa-search"></i>
          </button>
        </div>
      </div>
    </div>

    <div v-if="loading" class="loading-spinner d-flex justify-content-center my-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>

    <div v-else-if="error" class="alert alert-danger" role="alert">
      <p>{{ error }}</p>
    </div>

    <div v-else class="table-responsive">
      <table class="table table-hover">
        <thead>
          <tr>
            <th @click="sortBy('id')" class="sortable">
              ID
              <i class="fas" :class="getSortIcon('id')"></i>
            </th>
            <th @click="sortBy('name')" class="sortable">
              Name
              <i class="fas" :class="getSortIcon('name')"></i>
            </th>
            <th @click="sortBy('display_name')" class="sortable">
              Display Name
              <i class="fas" :class="getSortIcon('display_name')"></i>
            </th>
            <th>Description</th>
            <th @click="sortBy('created_at')" class="sortable">
              Created At
              <i class="fas" :class="getSortIcon('created_at')"></i>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="permission in filteredPermissions" :key="permission.id">
            <td>{{ permission.id }}</td>
            <td>{{ permission.name }}</td>
            <td>{{ permission.display_name || '-' }}</td>
            <td>{{ permission.description || '-' }}</td>
            <td>{{ formatDate(permission.created_at) }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import apiService from '@/services/api';

export default {
  name: 'PermissionsTable',
  setup() {
    const permissions = ref([]);
    const loading = ref(true);
    const error = ref(null);
    const searchQuery = ref('');
    const sortField = ref('id');
    const sortDirection = ref('asc');

    const fetchPermissions = async () => {
      loading.value = true;
      error.value = null;
      
      try {
        const response = await apiService.permissions.getAll();
        
        if (response.data && response.data.success) {
          permissions.value = response.data.data;
        } else {
          error.value = 'Failed to load permissions: Invalid response format';
        }
      } catch (err) {
        console.error('Error fetching permissions:', err);
        error.value = `Failed to load permissions: ${err.message || 'Unknown error'}`;
      } finally {
        loading.value = false;
      }
    };

    const filteredPermissions = computed(() => {
      let result = [...permissions.value];
      
      // Apply search filter
      if (searchQuery.value) {
        const query = searchQuery.value.toLowerCase();
        result = result.filter(permission => 
          permission.name.toLowerCase().includes(query) ||
          (permission.display_name && permission.display_name.toLowerCase().includes(query)) ||
          (permission.description && permission.description.toLowerCase().includes(query))
        );
      }
      
      // Apply sorting
      result.sort((a, b) => {
        const aValue = a[sortField.value];
        const bValue = b[sortField.value];
        
        if (sortDirection.value === 'asc') {
          return aValue > bValue ? 1 : -1;
        } else {
          return aValue < bValue ? 1 : -1;
        }
      });
      
      return result;
    });

    const sortBy = (field) => {
      if (sortField.value === field) {
        sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc';
      } else {
        sortField.value = field;
        sortDirection.value = 'asc';
      }
    };

    const getSortIcon = (field) => {
      if (sortField.value !== field) return 'fa-sort';
      return sortDirection.value === 'asc' ? 'fa-sort-up' : 'fa-sort-down';
    };

    const formatDate = (dateString) => {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
    };

    onMounted(() => {
      fetchPermissions();
    });

    return {
      permissions,
      loading,
      error,
      searchQuery,
      filteredPermissions,
      sortBy,
      getSortIcon,
      formatDate
    };
  }
};
</script>

<style scoped>
.permissions-table-container {
  background-color: #fff;
  border-radius: 0.5rem;
  padding: 1.5rem;
  box-shadow: 0 0.125rem 0.25rem rgba(0,0,0,0.075);
}

.table-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #333;
  margin: 0;
}

.table-controls {
  width: 300px;
}

.sortable {
  cursor: pointer;
  user-select: none;
}

.sortable:hover {
  background-color: #f8f9fa;
}

.table th {
  font-weight: 600;
  color: #495057;
  border-top: none;
}

.table td {
  vertical-align: middle;
}

.table-hover tbody tr:hover {
  background-color: #f8f9fa;
}

.input-group {
  box-shadow: 0 0.125rem 0.25rem rgba(0,0,0,0.075);
}

.form-control:focus {
  box-shadow: none;
  border-color: #80bdff;
}
</style> 